import React from 'react';
import PropTypes from 'prop-types';
import InfoButton from '../InfoButton';
import './HeadingSmall.css';

/**
 * HeadingSmall — Configurator
 *
 * Compact section heading row used in product configurators.
 * Layout:  Title  / SELECTOR (?)  Extra Info                 +49,-
 *
 * Figma node: 3815:1534
 * Toggle props: showSelection, showExtraInfo
 */

const HeadingSmall = ({
  title = 'Title',
  selection,
  separator = '/',
  price,
  extraInfo,
  showSelection = true,
  showInfoButton = true,
  showExtraInfo = true,
  infoTooltip,
  onInfoClick,
  className = '',
  ...props
}) => {
  // The info "?" appears when there's a hover tooltip OR a click handler (popup),
  // so attributes that only carry a Hint HTML popup still surface the button.
  const showInfo = showInfoButton && (infoTooltip || onInfoClick);
  const hasInfo = showSelection || showInfo;

  return (
    <div className={`cfg-heading-sm ${className}`.trim()} {...props}>
      <div className="cfg-heading-sm__row">
        {/* Left group: title + selection info + extra */}
        <div className="cfg-heading-sm__left">
          <span className="cfg-heading-sm__title typo-h4">{title}</span>

          {hasInfo && (
            <div className="cfg-heading-sm__selection-wrap">
              {showSelection && selection && (
                <span className="cfg-heading-sm__selection">
                  <span className="cfg-heading-sm__separator typo-body-sm">{separator}</span>
                  <span className="cfg-heading-sm__selection-label typo-h6">
                    {selection}
                  </span>
                </span>
              )}

              {showInfo && (
                <InfoButton
                  tooltip={infoTooltip}
                  onClick={onInfoClick}
                />
              )}
            </div>
          )}

          {showExtraInfo && extraInfo && (
            <span className="cfg-heading-sm__extra typo-body-sm">
              {extraInfo}
            </span>
          )}
        </div>

        {/* Right: price */}
        {price && (
          <span className="cfg-heading-sm__price typo-body-sm">{price}</span>
        )}
      </div>
    </div>
  );
};

HeadingSmall.propTypes = {
  /** Heading title text */
  title: PropTypes.string,
  /** Currently selected option label (displayed after the separator) */
  selection: PropTypes.string,
  /** Glyph between title and selection — "/" by default, "•" in the M2 (Overall) headings */
  separator: PropTypes.string,
  /** Price text (e.g. "+49,-") */
  price: PropTypes.string,
  /** Extra information text */
  extraInfo: PropTypes.string,
  /** Show the selection label cluster */
  showSelection: PropTypes.bool,
  /** Show the info button when infoTooltip is present */
  showInfoButton: PropTypes.bool,
  /** Show extra info text */
  showExtraInfo: PropTypes.bool,
  /** Tooltip text for the info button */
  infoTooltip: PropTypes.string,
  /** Click handler for the info button */
  onInfoClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default HeadingSmall;
